import type { ActivityOption, Interaction, LoungeRound } from "../types";
import {
  activityEnd,
  activityStart,
  calcOrderToReceivedTime,
  calcTotalDelayTime,
  calcTotalWaitTime,
  expressWaitFactor,
  interactionTimelineEvents,
} from "./tripDisplay";
import type { SegmentDayLookup } from "./tripDisplay";
import { inheritedActivityParticipants } from "./participants";
import type { SegmentParticipantsLookup } from "./participants";

/**
 * One past activity at an entity, reduced to the numbers the history view
 * reports. Built once per activity so the summary and the visit list agree
 * on what a wait or a tier was.
 */
export interface EntityVisitStats {
  id: string;
  tripId: string;
  segmentId: string | null;
  /** When it happened — first event, else the segment's day, else `createdAt`. */
  start: Date | null;
  /** Last event, or null when the activity has no span of its own. */
  end: Date | null;
  /** Seconds from first to last event; 0 when there is no span. */
  durationSec: number;
  /** Minutes, as posted at the entrance; null when none was recorded. */
  postedWait: number | null;
  /** Seconds actually spent in the queue (see `calcTotalWaitTime`). */
  actualWaitSec: number;
  delaySec: number;
  /** Seconds from ordering to the order arriving; 0 when not tracked. */
  serviceSec: number;
  tierId: string | null;
  /**
   * The tier as it reads on the entity today. Falls back to the stored id
   * when the option was removed, so an old visit still groups with itself.
   */
  tierName: string | null;
  /** Expected express wait in minutes, for express-style tiers with a posted wait. */
  expectedWait: number | null;
}

/** Label used for the bucket of visits that carry no access tier. */
export const UNTIERED_LABEL = "No tier";

export function entityVisitStats(
  activity: Interaction & { id: string },
  options: ActivityOption[] | null | undefined,
  onDay?: SegmentDayLookup,
): EntityVisitStats {
  const rounds = (activity.customResponses as Record<string, unknown> | undefined)?.rounds as
    | LoungeRound[]
    | undefined;
  const events = interactionTimelineEvents(activity.events, rounds);
  const start = activityStart(activity, onDay);
  const end = activityEnd(activity, onDay);
  const hasSpan = !!start && !!end && end.getTime() > start.getTime();
  const tierId = activity.accessTierId ?? null;
  const tierName = tierId
    ? (options?.find((o) => o.id === tierId)?.displayName ?? tierId)
    : null;
  const postedWait =
    activity.postedWait != null && activity.postedWait > 0 ? activity.postedWait : null;
  const factor = expressWaitFactor(tierName);
  return {
    id: activity.id,
    tripId: activity.tripId,
    segmentId: activity.segmentId ?? null,
    start,
    end: hasSpan ? end : null,
    durationSec: hasSpan ? (end!.getTime() - start!.getTime()) / 1000 : 0,
    postedWait,
    actualWaitSec: calcTotalWaitTime(events),
    delaySec: calcTotalDelayTime(events),
    serviceSec: calcOrderToReceivedTime(events),
    tierId,
    tierName,
    expectedWait: postedWait != null && factor != null ? postedWait * factor : null,
  };
}

export interface EntityTierSummary {
  /** Tier display name, or `UNTIERED_LABEL`. */
  name: string;
  count: number;
  lastVisit: Date | null;
  /** Minutes, over visits that recorded a posted wait; null when none did. */
  avgPosted: number | null;
  /** Seconds, over visits that recorded a real wait; null when none did. */
  avgActual: number | null;
  /** Minutes: `avgPosted` scaled by the express factor for express-style tiers. */
  expected: number | null;
  totalDelay: number;
}

export interface EntityHistorySummary {
  visits: number;
  firstVisit: Date | null;
  lastVisit: Date | null;
  /** Minutes, over visits that recorded a posted wait. */
  avgPostedWait: number | null;
  /** Seconds, over visits that recorded a real wait. */
  avgActualWait: number | null;
  /** Seconds, over visits with a span. */
  avgDuration: number | null;
  /** Seconds, over visits that tracked an order. */
  avgService: number | null;
  totalDelay: number;
  /** Busiest tier first. Empty when no visit carries a tier. */
  tiers: EntityTierSummary[];
}

function average(values: number[]): number | null {
  if (values.length === 0) return null;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

function latest(dates: (Date | null)[]): Date | null {
  let best: Date | null = null;
  for (const d of dates) {
    if (d && (!best || d.getTime() > best.getTime())) best = d;
  }
  return best;
}

function earliest(dates: (Date | null)[]): Date | null {
  let best: Date | null = null;
  for (const d of dates) {
    if (d && (!best || d.getTime() < best.getTime())) best = d;
  }
  return best;
}

/**
 * Roll a set of visits up into the headline numbers and a per-tier breakdown.
 *
 * Tiers group by *name* rather than option id, the way the trip analytics do,
 * so a tier that was deleted and re-added keeps one row. The untiered bucket
 * is included only when at least one visit is tiered: on an entity where
 * nobody ever picks a tier the breakdown would just repeat the headline.
 */
export function summarizeEntityHistory(visits: EntityVisitStats[]): EntityHistorySummary {
  const starts = visits.map((v) => v.start);
  const posted = visits.flatMap((v) => (v.postedWait != null ? [v.postedWait] : []));
  const actual = visits.flatMap((v) => (v.actualWaitSec > 0 ? [v.actualWaitSec] : []));
  const durations = visits.flatMap((v) => (v.durationSec > 0 ? [v.durationSec] : []));
  const service = visits.flatMap((v) => (v.serviceSec > 0 ? [v.serviceSec] : []));

  const buckets = new Map<string, EntityVisitStats[]>();
  for (const v of visits) {
    const key = v.tierName ?? UNTIERED_LABEL;
    const list = buckets.get(key) ?? [];
    list.push(v);
    buckets.set(key, list);
  }
  const anyTiered = visits.some((v) => v.tierName != null);
  const tiers: EntityTierSummary[] = anyTiered
    ? [...buckets.entries()]
        .map(([name, list]) => {
          const avgPosted = average(list.flatMap((v) => (v.postedWait != null ? [v.postedWait] : [])));
          const factor = name === UNTIERED_LABEL ? null : expressWaitFactor(name);
          return {
            name,
            count: list.length,
            lastVisit: latest(list.map((v) => v.start)),
            avgPosted,
            avgActual: average(list.flatMap((v) => (v.actualWaitSec > 0 ? [v.actualWaitSec] : []))),
            expected: avgPosted != null && factor != null ? avgPosted * factor : null,
            totalDelay: list.reduce((sum, v) => sum + v.delaySec, 0),
          };
        })
        .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
    : [];

  return {
    visits: visits.length,
    firstVisit: earliest(starts),
    lastVisit: latest(starts),
    avgPostedWait: average(posted),
    avgActualWait: average(actual),
    avgDuration: average(durations),
    avgService: average(service),
    totalDelay: visits.reduce((sum, v) => sum + v.delaySec, 0),
    tiers,
  };
}

/** Newest first; visits with no date at all sink to the bottom. */
export function orderVisitsNewestFirst<T extends { start: Date | null }>(visits: T[]): T[] {
  return [...visits].sort((a, b) => {
    if (!a.start && !b.start) return 0;
    if (!a.start) return 1;
    if (!b.start) return -1;
    return b.start.getTime() - a.start.getTime();
  });
}

/**
 * The travel-party keys that are this user in a trip. Normally one; the
 * party is keyed by member, and a member is linked to at most one account,
 * but nothing stops the same account appearing twice in a hand-built party.
 */
export function partyKeysForUser(
  travelParty: Record<string, { userId: string | null }> | null | undefined,
  uid: string | null | undefined,
): string[] {
  if (!travelParty || !uid) return [];
  return Object.entries(travelParty)
    .filter(([, m]) => m?.userId === uid)
    .map(([key]) => key);
}

interface ParticipantActivityLike {
  id: string;
  segmentId?: string | null;
  parentInteractionId?: string | null;
  participants?: string[] | null;
}

/**
 * Who was on a visit, resolved the way the activity page shows "who was
 * here": the activity's own list, else the nearest enclosing activity that
 * names one, else the segment chain, else the whole party.
 *
 * `related` must hold the activity and whatever ancestors it has, or the
 * inherited group falls through to the segment early.
 */
export function visitParticipants(
  activity: ParticipantActivityLike,
  related: ParticipantActivityLike[],
  segmentLookup: SegmentParticipantsLookup,
  allMemberIds: string[],
): string[] {
  const own = resolveOwnParticipants(activity, allMemberIds);
  if (own.length > 0) return own;
  return inheritedActivityParticipants(activity, related, segmentLookup, allMemberIds).participants;
}

/**
 * Whether the reader was there. A reader with no place in the party at all
 * was not, whatever the lists say.
 */
export function activityIncludesUser(
  activity: ParticipantActivityLike,
  related: ParticipantActivityLike[],
  segmentLookup: SegmentParticipantsLookup,
  allMemberIds: string[],
  userKeys: string[],
): boolean {
  if (userKeys.length === 0) return false;
  const there = visitParticipants(activity, related, segmentLookup, allMemberIds);
  return userKeys.some((k) => there.includes(k));
}

function resolveOwnParticipants(
  activity: ParticipantActivityLike,
  allMemberIds: string[],
): string[] {
  if (!Array.isArray(activity.participants) || activity.participants.length === 0) return [];
  const named = new Set(activity.participants);
  return allMemberIds.filter((id) => named.has(id));
}
